import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {ROUTE_SUFFIXES} from './constants/route-suffixes'
import {VIEW_IDS} from './constants/view-constants'
import {QUERIES} from './known-queries'

const ownerWithOptionalUnderscore = '([a-zA-Z0-9]+(?:_[a-zA-Z0-9]+)?(-[a-zA-Z0-9]+|-)*)'
export const issuesAtRepoIndexUrl = new RegExp(`^/${ownerWithOptionalUnderscore}/(([._a-zA-Z0-9-])*)/issues`)
export const issuesAtLabelIndexUrl = new RegExp(`^/${ownerWithOptionalUnderscore}/(([._a-zA-Z0-9-])*)/labels`)
export const issuesAtLabelShowUrl = new RegExp(
  `^/${ownerWithOptionalUnderscore}/(([._a-zA-Z0-9-])*)/labels/([._a-zA-Z0-9-]+)`,
)
export const issuesAtMilestoneShowUrl = new RegExp(
  `^/${ownerWithOptionalUnderscore}/(([._a-zA-Z0-9-])*)/milestone/([0-9]+)`,
)

/**
 * Regular expression to match boolean operators in the query string
 * For more details on boolean operators see:
 * https://docs.github.com/en/issues/tracking-your-work-with-issues/using-issues/filtering-and-searching-issues-and-pull-requests
 */
const booleanOperators = ['AND', 'OR'].join('|') // can be extended in the future if needed
const repeatedOperatorRuns = new RegExp(`\\b(?:${booleanOperators})(?:\\s+\\b(?:${booleanOperators})\\b)+`, 'gi')
const leadingOperators = new RegExp(`(^|\\()\\s*(?:${booleanOperators})\\b\\s*`, 'gi')
const trailingOperators = new RegExp(`\\b(?:${booleanOperators})\\b\\s*(\\)|$)`, 'gi')
const groupedOperators = new RegExp(`\\((?:[^)]*\\b(?:${booleanOperators})\\b[^)]*)\\)`, 'i')

type QueryField = 'label' | 'author' | 'assignee' | 'org' | 'repo' | 'type' | 'parent-issue' | 'is'

export function buildCopilotIssueAppLink({owner, repo, number}: {owner: string; repo: string; number: number}) {
  return `ghapp://github.com/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/issues/${number}`
}

export const searchUrl = ({viewId, query}: {viewId?: string; query?: string}) => {
  let newQuery = ''

  if (viewId !== VIEW_IDS.repository) {
    if (viewId && viewId !== VIEW_IDS.empty) {
      if (ROUTE_SUFFIXES[viewId]) {
        newQuery = `/${ROUTE_SUFFIXES[viewId]}`
      } else {
        newQuery = `/${viewId}`
      }
    } else {
      if (query === QUERIES.assignedToMe) {
        return `/issues/${ROUTE_SUFFIXES.assigned}`
      } else if (query === QUERIES.mentioned) {
        return `/issues/${ROUTE_SUFFIXES.mentioned}`
      } else if (query === QUERIES.createdByMe) {
        return `/issues/${ROUTE_SUFFIXES.createdByMe}`
      } else if (query === QUERIES.recentActivity) {
        return `/issues/${ROUTE_SUFFIXES.recentActivity}`
      }
    }
  }

  if (query !== undefined && query.trim() !== '') {
    newQuery = `${newQuery}?q=${encodeURIComponent(query)}`
  }

  if (viewId === VIEW_IDS.repository) {
    // this is when hyperlist is running at the repository level
    const path = ssrSafeLocation?.pathname
    let match = path.match(issuesAtRepoIndexUrl)

    if (match) {
      return path + newQuery
    }
    match =
      path.match(issuesAtLabelIndexUrl) || path.match(issuesAtLabelShowUrl) || path.match(issuesAtMilestoneShowUrl)
    if (match) {
      return `/${match[1]}/${match[3]}/issues${newQuery}`
    }
  }

  return `/issues${newQuery}`
}

function formatFieldValue(value: string): string {
  return value.indexOf(' ') > -1 ? `"${value}"` : value
}

/**
 * Balances boolean operators in the search query string
 * @param q - The search query string to balance
 * @returns The balanced search query string
 * @example
 * balanceBooleanOperators('is:issue OR OR label:bug AND AND author:monalisa')
 * // Returns: 'is:issue AND author:monalisa'
 *
 * balanceBooleanOperators('OR is:issue label:bug')
 * // Returns: 'is:issue label:bug'
 *
 * balanceBooleanOperators('is:issue label:bug AND')
 * // Returns: 'is:issue label:bug'
 */
function balanceBooleanOperators(q: string): string {
  let query = `${q}`.trim()
  if (!query) return query // empty query, nothing to normalize

  // Normalize boolean operators in the query string
  // Steps:

  // 1) Collapse mixed or repeated runs like "OR OR", "AND OR AND" -> keep the LAST operator in the run
  query = query.replace(repeatedOperatorRuns, m => {
    const ops: string[] = m.match(new RegExp(`\\b(?:${booleanOperators})\\b`, 'gi')) || []

    if (ops && ops.length) {
      const lastOp = ops[ops.length - 1]
      if (lastOp) return ` ${lastOp.toUpperCase()} `
    }

    return ' '
  })

  // 2) Remove leading operators and operators right after '('
  query = query.replace(leadingOperators, '$1')

  // 3) Remove trailing operators and operators right before ')'
  query = query.replace(trailingOperators, '$1')

  // 4) Remove any empty consecutive spaces created by the above operations
  query = query.replace(/\s+/g, ' ').trim()

  return query
}

/**
 * Evaluates if a search query is "complex"
 * @param q - The search query string to evaluate
 * @returns True if the query is complex, false otherwise
 * A "complex" query is defined as one that:
 * - Contains mixed boolean operators (AND and OR)
 * - Contains nested parentheses
 * - Contains grouped boolean expressions with parentheses
 * - Has obvious syntax issues like leading/trailing operators or repeated operators
 * Complex queries are better handled by additive logic when modifying them, to avoid breaking the user's intent
 * @example
 * isComplexQuery('is:issue label:bug') // false
 * isComplexQuery('is:issue OR label:bug AND author:monalisa') // true (mixed operators)
 * isComplexQuery('(is:issue OR is:pr) AND label:bug') // true (grouped with operators)
 * isComplexQuery('((is:issue OR is:pr) AND label:bug) OR author:monalisa') // true (nested parentheses)
 * isComplexQuery('OR is:issue label:bug') // true (leading operator)
 * isComplexQuery('is:issue label:bug AND') // true (trailing operator)
 * isComplexQuery('is:issue AND AND label:bug') // true (repeated operators)
 */
function isComplexQuery(q: string): boolean {
  const query = String(q || '')

  // 1) Boolean operators present?
  const [AND, OR] = booleanOperators.split('|')
  const hasAND = new RegExp(`\\b${AND}\\b`, 'i').test(query)
  const hasOR = new RegExp(`\\b${OR}\\b`, 'i').test(query)
  const hasAnyOp = hasAND || hasOR

  // 2) Parentheses and nesting depth
  let depth = 0
  let maxDepth = 0
  for (const ch of query) {
    if (ch === '(') {
      depth++
      if (depth > maxDepth) maxDepth = depth
    } else if (ch === ')') {
      depth = Math.max(0, depth - 1)
    }
  }

  const hasParens = maxDepth > 0
  const nestedParens = maxDepth > 1

  // 3) Obvious boolean syntax issues (we often balance/clean these)
  const hasLeadingOp = leadingOperators.test(query) // e.g., "(OR is:issue"
  const hasTrailingOp = trailingOperators.test(query) // e.g., "is:issue OR)"
  const hasDupOps = repeatedOperatorRuns.test(query) // e.g., "OR OR"

  // 4) Multiple boolean groups like "(...) AND (...)" or "(...) OR (...)"
  const hasGroupedWithOps = groupedOperators.test(query)

  const mixedOps = hasAND && hasOR

  // Heuristic: complex if any of these trip
  return (
    hasLeadingOp ||
    hasTrailingOp ||
    hasDupOps ||
    (hasAnyOp && hasParens) ||
    mixedOps ||
    nestedParens ||
    hasGroupedWithOps
  )
}

/**
 * Removes all instances of the given field from the query, then adds the field with the new value
 * @param query - The search query string to modify
 * @param field - The field name to replace (eg. 'label', 'assignee', 'is')
 * @param value - The new value for the field
 * @returns The modified query string, or undefined if the original query was empty
 * @example
 * replaceInQuery('is:open label:bug label:feature', 'label', 'enhancement')
 * // Returns: 'is:open label:enhancement'
 */
export function replaceInQuery(query: string, field: QueryField, value: string) {
  if (!query) {
    return undefined
  }
  // Matches `field:` followed by quoted values or unquoted values (eg. `label:bug` or `label:"needs review"`)
  const regex = new RegExp(`(?:^|\\s)${field}:(?:\\"[^\\"]*\\"|\\S+)`, 'g')
  const newQuery = query.replaceAll(regex, '').replace(/^\s+/, '')
  const newValue = formatFieldValue(value)

  return `${newQuery.trim()} ${field}:${newValue}`
}

/**
 * Removes any existing instance of the specific field:value combination from the query, then appends it to the end
 * @param query - The search query string to modify
 * @param field - The field name to add (eg. 'label', 'assignee', 'is')
 * @param value - The value for the field
 * @returns The modified query string with the field:value moved to the end, or just the field:value if the original query was empty
 * @example
 * addToQuery('is:open', 'label', 'bug')
 * // Returns: 'is:open label:bug'
 *
 * addToQuery('is:open label:bug author:monalisa', 'label', 'bug')
 * // Returns: 'is:open author:monalisa label:bug'
 */
export function addToQuery(query: string, field: QueryField, value: string) {
  if (!query) {
    return `${field}:${value}`
  }

  const targetValue = formatFieldValue(value)
  // Escapes special characters in the value to match literally (eg. `c++` becomes `c\\+\\+`)
  const escapedValue = targetValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  // Matches exact `field:value` at word boundaries (eg. `label:bug` but not `label:bugfix`)
  const fieldValueRegex = new RegExp(`(?:^|\\s)(${field}:${escapedValue})(?=\\s|$)`, 'g')

  const newQuery = query.replaceAll(fieldValueRegex, '').replace(/^\s+/, '')
  const newValue = formatFieldValue(value)
  return balanceBooleanOperators(`${newQuery.trim()} ${field}:${newValue}`)
}

/**
 * Creates a URL for an issue query, replacing any existing instances of the field
 * @param activeSearchQuery - The current query string to modify
 * @param scopedRepository - Whether to include the repository viewId in the URL
 * @param field - The field name to use in the query
 * @param value - The value for the field
 * @returns The complete URL for the issue query with replaced field
 * @example
 * createReplaceQueryFieldUrl('is:issue label:bug label:enhancement', true, 'label', 'feature')
 * // Returns: '/org/repo/issues?q=is%3Aissue%20label%3Afeature'
 */
export const createReplaceQueryFieldUrl = (
  activeSearchQuery: string,
  scopedRepository: boolean,
  field: QueryField,
  value: string,
) => {
  const queryFunction = isComplexQuery(activeSearchQuery) ? addToQuery : replaceInQuery
  return searchUrl({
    viewId: scopedRepository ? VIEW_IDS.repository : undefined,
    query: queryFunction(activeSearchQuery, field, value),
  })
}

/**
 * Creates a URL for an issue query, preserving existing instances of the field and adding the new one
 * @param activeSearchQuery - The current query string to modify
 * @param scopedRepository - Whether to include the repository viewId in the URL
 * @param field - The field name to use in the query
 * @param value - The value for the field
 * @returns The complete URL for the issue query with added field
 * @example
 * createAdditiveQueryFieldUrl('is:issue author:monalisa', false, 'is', 'blocked')
 * // Returns: '/issues?q=is%3Aissue%20author%3Amonalisa%20is%3Ablocked'
 */
export const createAdditiveQueryFieldUrl = (
  activeSearchQuery: string,
  scopedRepository: boolean,
  field: QueryField,
  value: string,
) => {
  return searchUrl({
    viewId: scopedRepository ? VIEW_IDS.repository : undefined,
    query: addToQuery(activeSearchQuery, field, value),
  })
}

const ISSUE_FIELD_PREFIX = 'field.'

/**
 * Sanitize an issue field name for use in a search qualifier
 */
function sanitizeIssueFieldName(name: string): string {
  return name.replaceAll(' ', '-')
}

/**
 * Always quote issue field values for the `field.<name>:"<value>"` qualifier
 * Escapes embedded backslashes and double quotes so they survive parsing
 */
function quoteIssueFieldValue(value: string): string {
  const escaped = value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
  return `"${escaped}"`
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Removes all existing `field.<fieldName>:...` qualifiers from the query, then appends the new one.
 * The value is always wrapped in double quotes to safely handle spaces, colons and other special chars.
 * @example
 * replaceIssueFieldInQuery('is:open field.Priority:p2 label:bug', 'Priority', 'p1')
 * // Returns: 'is:open label:bug field.Priority:"p1"'
 */
export function replaceIssueFieldInQuery(query: string, fieldName: string, value: string): string | undefined {
  if (!query) return undefined
  const slug = sanitizeIssueFieldName(fieldName)
  const slugRegex = escapeRegex(slug)
  // Match the field qualifier followed by a quoted value (with optional escaped chars) or an unquoted bare value
  const regex = new RegExp(`(?:^|\\s)field\\.${slugRegex}:(?:"(?:\\\\.|[^"\\\\])*"|\\S+)`, 'g')
  const newQuery = query.replaceAll(regex, '').replace(/^\s+/, '').trim()
  return `${newQuery} ${ISSUE_FIELD_PREFIX}${slug}:${quoteIssueFieldValue(value)}`.trim()
}

/**
 * Removes any existing instance of the specific `field.<name>:"<value>"` term from the query, then appends it.
 * Use when the query is complex (boolean operators / groups) and a naive replace would break it.
 */
export function addIssueFieldToQuery(query: string, fieldName: string, value: string): string {
  const slug = sanitizeIssueFieldName(fieldName)
  const term = `${ISSUE_FIELD_PREFIX}${slug}:${quoteIssueFieldValue(value)}`
  if (!query) return term
  const termRegex = new RegExp(`(?:^|\\s)(${escapeRegex(term)})(?=\\s|$)`, 'g')
  const newQuery = query.replaceAll(termRegex, '').replace(/^\s+/, '').trim()
  return balanceBooleanOperators(`${newQuery} ${term}`)
}

/**
 * Creates a URL for an issue query, replacing any existing instances of `field.<name>:...`
 * @example
 * createReplaceIssueFieldUrl('is:issue field.Priority:p2', true, 'Priority', 'p1')
 * // Returns: '/org/repo/issues?q=is%3Aissue%20field.Priority%3A%22p1%22'
 */
export const createReplaceIssueFieldUrl = (
  activeSearchQuery: string,
  scopedRepository: boolean,
  fieldName: string,
  value: string,
) => {
  const queryFunction = isComplexQuery(activeSearchQuery) ? addIssueFieldToQuery : replaceIssueFieldInQuery
  return searchUrl({
    viewId: scopedRepository ? VIEW_IDS.repository : undefined,
    query: queryFunction(activeSearchQuery, fieldName, value),
  })
}
